import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Typography,
} from "@material-ui/core";
import { Alert, AlertTitle } from "@material-ui/lab";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useGetABTestMetricsQuery } from "../../Queries/ABTestQueries";

function getMetricsErrorDetails(error: unknown): { status?: number; detail: string } {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const data = error.response?.data as
      | { detail?: string; title?: string; message?: string }
      | string
      | undefined;
    const detail =
      (typeof data === "object" && (data?.detail || data?.title || data?.message)) ||
      (typeof data === "string" && data) ||
      error.response?.statusText ||
      error.message ||
      "Unknown error";
    return { status, detail };
  }
  return { detail: error instanceof Error ? error.message : "Unknown error" };
}

export default function ABTestMetricsModal({
  featureKey,
  featureName,
  closeModal,
}: {
  featureKey: string;
  featureName: string;
  closeModal: () => void;
}) {
  const { clientId } = useParams<{ clientId: string }>();
  const { data, isLoading, isError, error } = useGetABTestMetricsQuery(clientId, featureKey);
  const errorDetails = isError ? getMetricsErrorDetails(error) : null;

  return (
    <>
      <DialogTitle>{featureName} Metrics</DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Typography variant="caption" component="p">
            Loading...
          </Typography>
        ) : isError ? (
          <Alert severity="error" data-test="ab-test-metrics-error">
            <AlertTitle>
              Failed to load metrics{errorDetails?.status ? ` (HTTP ${errorDetails.status})` : ""}
            </AlertTitle>
            {errorDetails?.detail}
          </Alert>
        ) : (
          <Box>
            <Grid container spacing={2} direction="row">
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" component="p">
                  Total Sessions:
                </Typography>
                <Typography variant="body1" component="p">
                  {data?.totalSessionCount ?? 0}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" component="p">
                  Total Job Applies:
                </Typography>
                <Typography variant="body1" component="p">
                  {data && data.variants.length > 0
                    ? data.variants.reduce((a, b) => a + (b.eventTypeCounts["jobapply"] ?? 0), 0)
                    : 0}
                </Typography>
              </Grid>
            </Grid>
            {data && data.variants.length > 0
              ? data.variants.map((variant) => {
                  const conversionRatePercentage = (variant.jobApplyConversionRate * 100).toFixed(2) + "%";
                  return (
                    <Grid key={variant.variantKey} container spacing={2} direction="row">
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" component="p">
                          Variant:
                        </Typography>
                        <Typography variant="body1" component="p" style={{ fontWeight: "bold" }}>
                          {variant.variantKey}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" component="p">
                          Sessions:
                        </Typography>
                        <Typography variant="body1" component="p">
                          {variant.sessionCount}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" component="p">
                          Job Apply Conversion:
                        </Typography>
                        <Typography variant="body1" component="p">
                          {conversionRatePercentage}
                        </Typography>
                      </Grid>
                    </Grid>
                  );
                })
              : null}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button type="submit" variant="outlined" onClick={closeModal}>
          Close
        </Button>
      </DialogActions>
    </>
  );
}
